import { useState } from 'react'
import { Link, createFileRoute } from '@tanstack/react-router'
import { ColumnDef } from '@tanstack/react-table'
import { ArrowLeft, Copy, Trash2, Shield, ShieldOff, ArrowRightLeft, Wallet, UserCog } from 'lucide-react'
import { DataTable, SortableHeader } from '@/components/ui/data-table'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { useQuery } from '@tanstack/react-query'
import { adminQueries } from '@/services/adminApi'
import { AdminCall, AdminTransaction } from '@/services/adminApi'
import { TRANSACTION_STATUS_LABELS, TRANSACTION_TYPE_LABELS, getStatusBadgeVariant } from '@/types/account'
import { CurrencyDisplay } from '@/components/ui/currency-display'
import AdjustBalanceDialog from '@/components/admin/AdjustBalanceDialog'
import UpdateRoleDialog from '@/components/admin/UpdateRoleDialog'
import DeleteAccountDialog from '@/components/admin/DeleteAccountDialog'
import BlockAccountDialog from '@/components/admin/BlockAccountDialog'
import MigrateAccountDialog from '@/components/admin/MigrateAccountDialog'
import { useRegenerateReferralCode } from '@/hooks/useAdmin'

const callColumns: ColumnDef<AdminCall, any>[] = [
  {
    accessorKey: 'id',
    header: ({ column }) => <SortableHeader column={column} label="ID" />,
  },
  {
    accessorKey: 'externalId',
    header: ({ column }) => <SortableHeader column={column} label="External ID" />,
    cell: ({ row }) => {
      const externalId = row.original.externalId
      if (!externalId) return '-'
      return (
        <span className="text-xs font-mono text-muted-foreground">
          {externalId}
        </span>
      )
    },
  },
  {
    accessorKey: 'originNumber',
    header: ({ column }) => <SortableHeader column={column} label="Origin" />,
    cell: ({ row }) => {
      return row.original.originNumber || '-'
    },
  },
  {
    accessorKey: 'destinationNumber',
    header: ({ column }) => <SortableHeader column={column} label="Destination" />,
  },
  {
    accessorKey: 'duration',
    header: ({ column }) => <SortableHeader column={column} label="Duration" />,
    cell: ({ row }) => {
      const seconds = row.original.duration
      if (seconds == null) return '-'
      const mins = Math.floor(seconds / 60)
      const secs = seconds % 60
      return `${mins}:${String(secs).padStart(2, '0')}`
    },
  },
  {
    id: 'cost',
    header: 'Cost',
    cell: ({ row }) => {
      const cost = row.original.cost
      if (!cost) return '-'
      return <CurrencyDisplay amount={cost.amount} currency={cost.currency} debit hideSign inCents />
    },
  },
  {
    accessorKey: 'status',
    header: ({ column }) => <SortableHeader column={column} label="Status" />,
    cell: ({ row }) => {
      const status = row.original.status
      const variant = status === 'completed' ? 'default' : status === 'failed' ? 'destructive' : 'secondary'
      return <Badge variant={variant}>{status}</Badge>
    },
  },
  {
    accessorKey: 'startTime',
    header: ({ column }) => <SortableHeader column={column} label="Date" />,
    cell: ({ row }) => {
      const startTime = row.original.startTime
      if (!startTime) return '-'
      return new Date(startTime).toLocaleString()
    },
  },
]

const transactionColumns: ColumnDef<AdminTransaction, any>[] = [
  {
    accessorKey: 'id',
    header: ({ column }) => <SortableHeader column={column} label="ID" />,
  },
  {
    accessorKey: 'externalId',
    header: ({ column }) => <SortableHeader column={column} label="External ID" />,
    cell: ({ row }) => {
      const externalId = row.original.externalId
      if (!externalId) return '-'
      return (
        <span className="text-xs font-mono text-muted-foreground">
          {externalId}
        </span>
      )
    },
  },
  {
    accessorKey: 'type',
    header: ({ column }) => <SortableHeader column={column} label="Type" />,
    cell: ({ row }) => {
      const type = row.original.type
      const label = TRANSACTION_TYPE_LABELS[type] || type
      return <Badge variant="outline">{label}</Badge>
    },
  },
  {
    id: 'amount',
    header: 'Amount',
    cell: ({ row }) => {
      const payment = row.original.payment
      if (!payment) return '-'
      return <CurrencyDisplay amount={payment.amount} currency={payment.currency} inCents />
    },
  },
  {
    accessorKey: 'status',
    header: ({ column }) => <SortableHeader column={column} label="Status" />,
    cell: ({ row }) => {
      const status = row.original.status
      const label = TRANSACTION_STATUS_LABELS[status] || status
      return <Badge variant={getStatusBadgeVariant(status)}>{label}</Badge>
    },
  },
  {
    accessorKey: 'createdAt',
    header: ({ column }) => <SortableHeader column={column} label="Created At" />,
    cell: ({ row }) => {
      const date = row.original.createdAt
      if (!date) return '-'
      return new Date(date).toLocaleString()
    },
  },
]

export const Route = createFileRoute('/admin/users/$accountId')({
  component: AdminUserDetailPage,
})

function AdminUserDetailPage() {
  const { accountId } = Route.useParams()
  const [callsPage, setCallsPage] = useState(1)
  const [txPage, setTxPage] = useState(1)
  const [adjustOpen, setAdjustOpen] = useState(false)
  const [updateRoleOpen, setUpdateRoleOpen] = useState(false)
  const [deleteAccountOpen, setDeleteAccountOpen] = useState(false)
  const [blockAccountOpen, setBlockAccountOpen] = useState(false)
  const [migrateAccountOpen, setMigrateAccountOpen] = useState(false)
  const regenerateReferralCode = useRegenerateReferralCode()
  const pageSize = 20

  const { data: accountData, isLoading: accountLoading } = useQuery(adminQueries.account(accountId!))
  const { data: callsData, isLoading: callsLoading } = useQuery(adminQueries.accountCalls(accountId!, callsPage, pageSize))
  const { data: txData, isLoading: txLoading } = useQuery(adminQueries.accountTransactions(accountId!, txPage, pageSize))

  if (accountLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-48 w-full" />
      </div>
    )
  }

  const account = accountData?.account
  const referralStats = accountData?.referralStats
  const referralLink = referralStats?.referralCode ? `${window.location.origin}/?ref=${encodeURIComponent(referralStats.referralCode)}` : ''

  if (!account) {
    return (
      <div className="space-y-4">
        <Link to="/admin/users" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4" /> Back to Users
        </Link>
        <p>Account not found.</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <Link to="/admin/users" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" /> Back to Users
      </Link>

      <Card>
        <CardHeader>
          <CardTitle>Account Details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4 lg:grid-cols-5">
            <div>
              <p className="text-sm text-muted-foreground">Email</p>
              <p className="font-medium">{account.email}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Role</p>
              <Badge variant={account.role === 'admin' ? 'default' : 'secondary'}>
                {account.role}
              </Badge>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Balance</p>
              <CurrencyDisplay amount={account.balance.amount} currency={account.balance.currency} inCents />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Status</p>
              <Badge variant={account.enabled ? 'default' : 'destructive'}>
                {account.enabled ? 'Active' : 'Blocked'}
              </Badge>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Created</p>
              <p className="font-medium">
                {new Date(account.createdAt).toLocaleDateString()}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Verified</p>
              <Badge variant={account.verified ? 'default' : 'secondary'}>
                {account.verified ? 'Verified' : 'Unverified'}
              </Badge>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Currency Locked</p>
              <Badge variant={account.currencyLocked ? 'outline' : 'secondary'}>
                {account.currencyLocked ? 'Locked' : 'Unlocked'}
              </Badge>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Referral Code</p>
              <p className="font-mono text-sm">{referralStats?.referralCode || '-'}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Referred By</p>
              <p className="font-mono text-sm">{account.referredCode || '-'}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Last Updated</p>
              <p className="font-medium">{new Date(account.lastUpdatedAt).toLocaleString()}</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" onClick={() => setAdjustOpen(true)}>
              <Wallet className="w-4 h-4 mr-1" />
              Adjust Balance
            </Button>
            <Button size="sm" onClick={() => {setUpdateRoleOpen(true)}}>
              <UserCog className="w-4 h-4 mr-1" />
              Update Role
            </Button>
            <Button
              onClick={() => setBlockAccountOpen(true)}
              variant="outline"
              size="sm"
            >
              {account.enabled ? (
                <>
                  <ShieldOff className="w-4 h-4 mr-1 text-destructive" />
                  Block
                </>
              ) : (
                <>
                  <Shield className="w-4 h-4 mr-1 text-green-500" />
                  Unblock
                </>
              )}
            </Button>
            <Button
              onClick={() => setDeleteAccountOpen(true)}
              variant="outline"
              size="sm"
            >
              <Trash2 className="w-4 h-4 mr-1 text-destructive" />
              Delete
            </Button>
            <Button
              onClick={() => setMigrateAccountOpen(true)}
              variant="outline"
              size="sm"
            >
              <ArrowRightLeft className="w-4 h-4 mr-1" />
              Migrate
            </Button>
            <Button
              onClick={() => regenerateReferralCode.mutate({ accountId: accountId! })}
              variant="outline"
              size="sm"
              disabled={regenerateReferralCode.isPending}
            >
              <Copy className="w-4 h-4 mr-1" />
              Regenerate Referral Code
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Referral Details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-4">
            <div>
              <p className="text-sm text-muted-foreground">People Referred</p>
              <p className="text-2xl font-semibold">{referralStats?.totalReferredCount ?? 0}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Total Earned</p>
              <CurrencyDisplay amount={referralStats?.totalEarnedCents ?? 0} currency={account.balance.currency} inCents hideSign />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Pending</p>
              <CurrencyDisplay amount={referralStats?.pendingEarnedCents ?? 0} currency={account.balance.currency} inCents hideSign />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Reward Paid At</p>
              <p className="font-medium">{account.referralRewardPaidAt ? new Date(account.referralRewardPaidAt).toLocaleString() : '-'}</p>
            </div>
          </div>

          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">Referral Link</p>
            <div className="flex gap-2">
              <Input readOnly value={referralLink} placeholder="No referral link available" />
              <Button
                variant="outline"
                disabled={!referralLink}
                onClick={() => referralLink && navigator.clipboard.writeText(referralLink)}
              >
                <Copy className="mr-1 h-4 w-4" />
                Copy
              </Button>
            </div>
          </div>

          {referralStats?.currentProgramme && (
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">Current Programme</p>
              <div className="grid gap-2 sm:grid-cols-2">
                {referralStats.currentProgramme.rewards.map((reward) => (
                  <div key={reward.currency} className="rounded-md border p-3 text-sm">
                    <div className="font-medium">{reward.currency}</div>
                    <div>Referrer: <CurrencyDisplay amount={reward.referrerCreditCents ?? 0} currency={reward.currency} inCents hideSign /></div>
                    <div>Referee: <CurrencyDisplay amount={reward.refereeCreditCents ?? 0} currency={reward.currency} inCents hideSign /></div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {referralStats?.affiliateApplication && (
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">Affiliate Profile</p>
              <div className="grid gap-4 sm:grid-cols-4">
                <div>
                  <p className="text-sm text-muted-foreground">Code</p>
                  <p className="font-mono text-sm">{referralStats.affiliateApplication.code}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Revenue Share</p>
                  <p className="font-medium">{(Number(referralStats.affiliateApplication.sharePercentage) * 100).toFixed(1)}%</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Duration</p>
                  <p className="font-medium">{referralStats.affiliateApplication.revenueShareMonths} months</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Affiliate Referrals</p>
                  <p className="font-medium">{referralStats.affiliateApplication.totalReferredCount ?? 0}</p>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <AdjustBalanceDialog
        open={adjustOpen}
        onOpenChange={setAdjustOpen}
        accountId={accountId!}
        currentBalance={account.balance.amount}
        currency={account.balance.currency}
      />

      <UpdateRoleDialog
        open={updateRoleOpen}
        onOpenChange={setUpdateRoleOpen}
        accountId={accountId!}
      />

      <DeleteAccountDialog
        open={deleteAccountOpen}
        onOpenChange={setDeleteAccountOpen}
        accountId={accountId!}
      />

      <BlockAccountDialog
        open={blockAccountOpen}
        onOpenChange={setBlockAccountOpen}
        accountId={accountId!}
        enabled={account.enabled}
      />

    <MigrateAccountDialog
        open={migrateAccountOpen}
        onOpenChange={setMigrateAccountOpen}
        accountId={accountId!}
    />

      <div className="space-y-4">
        <h2 className="text-2xl font-bold">Calls</h2>
        {callsLoading ? (
          <Skeleton className="h-64 w-full" />
        ) : (
          <DataTable
            columns={callColumns}
            data={callsData?.calls ?? []}
            enableSearch={false}
            manualPagination={{
              pageIndex: callsPage,
              pageSize,
              totalItems: callsData?.totalItems ?? 0,
              onPageChange: setCallsPage,
              isLoading: callsLoading,
            }}
          />
        )}
      </div>

      <div className="space-y-4">
        <h2 className="text-2xl font-bold">Transactions</h2>
        {txLoading ? (
          <Skeleton className="h-64 w-full" />
        ) : (
          <DataTable
            columns={transactionColumns}
            data={txData?.transactions ?? []}
            enableSearch={false}
            manualPagination={{
              pageIndex: txPage,
              pageSize,
              totalItems: txData?.totalItems ?? 0,
              onPageChange: setTxPage,
              isLoading: txLoading,
            }}
          />
        )}
      </div>
    </div>
  )
}
